import React, { useEffect, useState } from "react";
import ApiService from "Settings/MemberManagement/memberApiServices";

export default function PermissionManager({ memberId, onClose }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [message, setMessage]   = useState(null);

  useEffect(() => {
    if (!memberId) return;
    fetchServices();
  }, [memberId]);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchServices = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const res = await ApiService.getServiceList(memberId);
      if (res?.status === false) {
        setMessage({ type: "error", text: res?.message || "Failed to load services." });
        setServices([]);
      } else {
        setServices(res?.data || []);
      }
    } catch (err) {
      setMessage({ type: "error", text: "Failed to load services." });
    } finally {
      setLoading(false);
    }
  };

  // ── Toggle ───────────────────────────────────────────────────────────────
  const toggleService = (id) => {
    setServices((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: !s.status } : s))
    );
  };

  const toggleAll = (val) => {
    setServices((prev) => prev.map((s) => ({ ...s, status: val })));
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Only send checked services
      const payload = services
        .filter((s) => s.status)
        .map((s) => ({ id: s.id }));

      const res = await ApiService.assignServiceList(memberId, payload);

      setMessage({
        type: res?.status ? "success" : "error",
        text: res?.data || res?.message || "Done",
      });
    } catch (err) {
      setMessage({ type: "error", text: "Save failed. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  // ── Group by category ─────────────────────────────────────────────────────
  const grouped = services.reduce((acc, svc) => {
    const cat = svc.category || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(svc);
    return acc;
  }, {});

  const allChecked  = services.length > 0 && services.every((s) => s.status);
  const noneChecked = services.length > 0 && services.every((s) => !s.status);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ maxWidth: 700 }}>
        <div className="modal-content border-0 rounded-3">

          {/* ── Header ── */}
          <div className="modal-header text-white" style={{ background: "#002970" }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold">
                <i className="ph-bold ph-shield-lock me-2" />
                Manage Permissions
              </h5>
              <small className="opacity-75">
                Only services assigned to you are shown
              </small>
            </div>
            <button className="btn-close btn-close-white" onClick={onClose} />
          </div>

          {/* ── Body ── */}
          <div className="modal-body p-4">

            {/* Alert */}
            {message && (
              <div className={`alert alert-${message.type === "success" ? "success" : "danger"} py-2 mb-3`}>
                {message.text}
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" />
                <p className="mt-2 text-muted">Loading services...</p>
              </div>
            ) : services.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="ph-duotone ph-inbox fs-1 d-block mb-2" />
                <p>No services available to assign.</p>
                <small>Your account may not have any services assigned yet.</small>
              </div>
            ) : (
              <>
                {/* Select all / none + counter */}
                <div className="d-flex gap-2 mb-3 align-items-center">
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => toggleAll(true)}
                    disabled={allChecked}
                  >
                    <i className="ph-bold ph-check me-1" />
                    Select All
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => toggleAll(false)}
                    disabled={noneChecked}
                  >
                    <i className="ph-bold ph-x me-1" />
                    Deselect All
                  </button>
                  <span className="ms-auto text-muted small">
                    {services.filter((s) => s.status).length} / {services.length} selected
                  </span>
                </div>

                {/* Grouped service cards */}
                {Object.entries(grouped).map(([category, items]) => (
                  <div key={category} className="mb-4">
                    {/* Category label */}
                    <div
                      className="fw-semibold text-uppercase small mb-2"
                      style={{
                        color: "#002970",
                        letterSpacing: "0.6px",
                        borderLeft: "3px solid #00B9F1",
                        paddingLeft: 8,
                      }}
                    >
                      {category}
                    </div>

                    <div className="row g-2">
                      {items.map((svc) => (
                        <div className="col-6 col-md-4" key={svc.id}>
                          <div
                            className={`border rounded-3 p-2 d-flex align-items-center gap-2 ${
                              svc.status
                                ? "border-primary bg-primary bg-opacity-10"
                                : "border-light bg-light"
                            }`}
                            style={{ cursor: "pointer", transition: "all 0.15s" }}
                            onClick={() => toggleService(svc.id)}
                          >
                            {/* Icon */}
                            {svc.photo ? (
                              <img
                                src={`https://api.mokshiq.in/media/${svc.photo}`}
                                alt={svc.name}
                                style={{ width: 32, height: 32, borderRadius: 8, objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                                  background: svc.status ? "#002970" : "#e0e0e0",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  color: svc.status ? "#fff" : "#999",
                                  fontSize: 14, fontWeight: 700,
                                }}
                              >
                                {svc.name?.[0]?.toUpperCase() ?? "?"}
                              </div>
                            )}

                            {/* Name */}
                            <span
                              className="small fw-semibold flex-grow-1"
                              style={{ color: svc.status ? "#002970" : "#555", lineHeight: 1.2 }}
                            >
                              {svc.name}
                            </span>

                            {/* Checkbox */}
                            <div
                              style={{
                                width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                                border: `2px solid ${svc.status ? "#002970" : "#ccc"}`,
                                background: svc.status ? "#002970" : "transparent",
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}
                            >
                              {svc.status && (
                                <svg width="10" height="10" viewBox="0 0 10 10">
                                  <path
                                    d="M1.5 5l2.5 2.5 4.5-4"
                                    stroke="#fff" strokeWidth="1.8"
                                    strokeLinecap="round" strokeLinejoin="round" fill="none"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="modal-footer border-top pt-3">
            <button className="btn btn-light" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn text-white fw-semibold px-4"
              style={{ background: "#002970" }}
              onClick={handleSave}
              disabled={saving || loading || services.length === 0}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="ph-bold ph-floppy-disk me-2" />
                  Save Permissions
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}